import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import Accordion from "react-bootstrap/Accordion";
import Button from "react-bootstrap/Button";
import Nav from "react-bootstrap/Nav";
import Spinner from "react-bootstrap/Spinner";
import ForeignSpendAccordion from "../components/ForeignSpendAccordion";
import ObigPurchaseDebtBar from "../components/ObigPurchaseDebtBar";
import {ADMIN_ONLY_CARDS} from "../constans";
import { dedupeBlackMonobankAccounts, formatBlackCreditUsageUa } from "../helpers/monobankBlackCard";
import { useFopLabels } from "../hooks/useFopLabels";

registerLocale("ru", ru);

const NOVAPAY_RETRY_DELAYS_MS = [5000, 15000, 30000];
const OBIG_TABS = [
  { id: "balance", slug: "balance", label: "Баланс коштів" },
  { id: "foreign", slug: "foreign", label: "Закордонні витрати (ліміт НБУ)" },
  { id: "fopLimits", slug: "fop-limits", label: "Ліміти ФОПів" },
];
const DEFAULT_OBIG_TAB = OBIG_TABS[0];
const OBIG_TAB_BY_ID = new Map(OBIG_TABS.map((tab) => [tab.id, tab]));
const OBIG_TAB_BY_SLUG = new Map(OBIG_TABS.map((tab) => [tab.slug, tab]));
const MONTH_LABELS_UA = ["січ", "лют", "бер", "кві", "тра", "чер", "лип", "сер", "вер", "жов", "лис", "гру"];
const CHART_BAR_MAX_HEIGHT = 120;
const CHART_AREA_HEIGHT = 128;
const UAH_CURRENCY_CODE = 980;
const USD_CURRENCY_CODE = 840;

const money0 = (v) => Math.round(Number(v || 0)).toLocaleString("uk-UA");

// Залишок року у місяцях: повні місяці після поточного + частка поточного за днями, що лишились (без сьогодні).
const getRemainingPeriodInYear = (year) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  if (year > currentYear) return { months: 12, days: 0, monthsFloat: 12 };
  if (year < currentYear) return { months: 0, days: 0, monthsFloat: 0 };
  const months = Math.max(0, 11 - now.getMonth());
  const daysInMonth = new Date(currentYear, now.getMonth() + 1, 0).getDate();
  const days = daysInMonth - now.getDate();
  return { months, days, monthsFloat: months + days / daysInMonth };
};

const parseMoneyInput = (value) => {
  const normalized = String(value || "")
      .replace(/\s/g, "")
      .replace(/[₴грн]/gi, "")
      .replace(",", ".");
  // Порожній рядок — не число: інакше Number("") === 0 і очищене поле зберігалось би як 0.
  if (normalized === "") return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const getMonoCurrencyCode = (account) => {
  const code = Number(account?.currencyCode);
  return Number.isFinite(code) ? code : UAH_CURRENCY_CODE;
};

const isUsdMonoCard = (account) => getMonoCurrencyCode(account) === USD_CURRENCY_CODE;
const isUahMonoCard = (account) => getMonoCurrencyCode(account) === UAH_CURRENCY_CODE;
const isRegularObigMonoCard = (account) =>
    account?.type === "white" || account?.type === "fop" || isUsdMonoCard(account);

const getMonoOwnBalanceMinor = (account) =>
    Number(account?.balance || 0) - Number(account?.creditLimit || 0);

const getMonoCardLast4 = (account) => {
  const pans = Array.isArray(account?.maskedPan) ? account.maskedPan : [];
  const raw = pans.find((pan) => /\d{4}$/.test(String(pan || "")));
  if (raw) return String(raw).slice(-4);
  const iban = String(account?.iban || "");
  return /\d{4}$/.test(iban) ? iban.slice(-4) : "";
};

const formatMonoCardLabel = (account, isUsdCard) => {
  const last4 = getMonoCardLast4(account);
  return `${isUsdCard ? "USD" : account?.type} card${last4 ? ` **${last4}` : ""}`;
};

// Горизонтальний скрол місяців: одразу центруємо поточний місяць, щоб не гортати вручну.
// Скролимо лише при монтуванні / зміні року, щоб збереження значення не збивало позицію користувача.
function FopMonthsScroller({ year, children }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const target = container.querySelector(`[data-month="${currentMonth}"]`);
    if (!target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset = targetRect.left - containerRect.left + container.scrollLeft;
    container.scrollLeft = Math.max(0, offset - (container.clientWidth - targetRect.width) / 2);
  }, [year]);

  return (
      <div
          ref={ref}
          className="d-flex align-items-end gap-3 overflow-auto pb-1"
          style={{ minHeight: 190, position: "relative", zIndex: 2 }}
      >
        {children}
      </div>
  );
}

function FopLimitsCards({ data, loading, error, labelBySlug, onSaveMonthValue, onResetMonthValue }) {
  const [draftValues, setDraftValues] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const next = {};
    (data?.items || []).forEach((item) => {
      (item.months || []).forEach((monthRow) => {
        next[`${item.key}:${monthRow.month}`] = money0(monthRow.displayNet ?? monthRow.net);
      });
    });
    setDraftValues(next);
  }, [data]);

  if (loading) {
    return (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" className="text-black" />
        </div>
    );
  }

  if (error) {
    return <div className="alert alert-warning">{error}</div>;
  }

  const items = data?.items || [];
  if (!items.length) {
    return <div className="text-muted">Немає даних по лімітах ФОПів</div>;
  }

  const maxMonthValue = Math.max(
      1,
      ...items.flatMap((item) =>
          (item.months || []).flatMap((m) => [m.totalIncome || 0, m.refunds || 0])
      )
  );

  return (
      <div className="d-flex flex-column gap-3">
        {items.map((item) => {
          const usedPercent = Math.min(100, ((item.used || 0) / (item.limit || 1)) * 100);
          const displayName = labelBySlug[item.key] || item.name || item.key;
          const monthlyBenchmark = (item.limit || 0) / 12;
          const remainingPeriod = getRemainingPeriodInYear(data?.year || new Date().getFullYear());
          const remainingAvg = remainingPeriod.monthsFloat > 0 ? (item.remaining || 0) / remainingPeriod.monthsFloat : 0;
          const remainingPeriodLabel = [
            remainingPeriod.months > 0 ? `${remainingPeriod.months} міс.` : "",
            remainingPeriod.days > 0 ? `${remainingPeriod.days} дн.` : "",
          ].filter(Boolean).join(" ");
          const benchmarkTop = CHART_AREA_HEIGHT - Math.min(CHART_BAR_MAX_HEIGHT, (monthlyBenchmark / maxMonthValue) * CHART_BAR_MAX_HEIGHT);

          return (
              <div
                  key={item.key}
                  className="bg-white rounded-4 shadow-sm p-3 p-md-4"
                  style={{ border: "1px solid rgba(0,0,0,.06)" }}
              >
                <div className="row g-4 align-items-stretch">
                  <div className="col-12 col-lg-4">
                    <div className="h-100 d-flex flex-column justify-content-center">
                      <h5 className="mb-3">{displayName}</h5>
                      <div className="text-muted small">Використано</div>
                      <div className="fs-5 fw-semibold">
                        {money0(item.used)} ₴ з {money0(item.limit)} ₴
                      </div>
                      <div className="progress mt-3" style={{ height: 10 }}>
                        <div
                            className={`progress-bar ${usedPercent > 90 ? "bg-danger" : usedPercent > 75 ? "bg-warning" : "bg-primary"}`}
                            style={{ width: `${usedPercent}%` }}
                        />
                      </div>
                      <div className="text-muted small mt-2">
                        Залишилось: {money0(item.remaining)} ₴
                      </div>
                      <div className="text-muted small mt-1">
                        Середнє до кінця року:{" "}
                        <strong>{remainingPeriod.monthsFloat > 0 ? money0(remainingAvg) : "0"} ₴/міс</strong>
                        {remainingPeriodLabel && <div className="fst-italic">({remainingPeriodLabel})</div>}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-8">
                    <div className="position-relative">
                      <div
                          className="position-absolute start-0 end-0 border-top border-primary"
                          style={{ top: benchmarkTop, opacity: 0.55, zIndex: 1 }}
                          title={`Середній місячний ліміт: ${money0(monthlyBenchmark)} ₴`}
                      />
                      <div
                          className="position-absolute end-0 text-primary small bg-white ps-1"
                          style={{ top: Math.max(0, benchmarkTop - 18), zIndex: 2 }}
                      >
                        {money0(monthlyBenchmark)} ₴/міс
                      </div>
                      <FopMonthsScroller year={data?.year}>
                        {(item.months || []).map((monthRow) => {
                        const inputKey = `${item.key}:${monthRow.month}`;
                        const monthIndex = Number(monthRow.month?.slice(5, 7) || 1) - 1;
                        const totalIncome = monthRow.totalIncome || 0;
                        const incomeHeight = totalIncome > 0 ? Math.max(8, (totalIncome / maxMonthValue) * CHART_BAR_MAX_HEIGHT) : 4;
                        const refundHeight = monthRow.refunds > 0 ? Math.max(8, (monthRow.refunds / maxMonthValue) * CHART_BAR_MAX_HEIGHT) : 4;
                        const monoPart = totalIncome > 0 ? (monthRow.monobankIncome || 0) / totalIncome : 0;
                        const novaPart = totalIncome > 0 ? (monthRow.novapayIncome || 0) / totalIncome : 0;
                        const cashPart = totalIncome > 0 ? (monthRow.cashIncome || 0) / totalIncome : 0;

                        return (
                            <div key={monthRow.month} data-month={monthRow.month} className="text-center flex-shrink-0" style={{ width: 72 }}>
                              <div className="d-flex align-items-end justify-content-center gap-2" style={{ height: 128 }}>
                                <div
                                    className="rounded-top overflow-hidden"
                                    title={`Надходження: ${money0(totalIncome)} ₴`}
                                    style={{
                                      width: 22,
                                      height: incomeHeight,
                                      minHeight: 4,
                                      background: "#e9ecef",
                                      display: "flex",
                                      flexDirection: "column-reverse",
                                    }}
                                >
                                  {monoPart > 0 && <div style={{ height: `${monoPart * 100}%`, background: "#111827" }} />}
                                  {novaPart > 0 && <div style={{ height: `${novaPart * 100}%`, background: "#315bdc" }} />}
                                  {cashPart > 0 && <div style={{ height: `${cashPart * 100}%`, background: "#f59f00" }} />}
                                </div>
                                <div
                                    className="rounded-top"
                                    title={`Повернення: ${money0(monthRow.refunds)} ₴`}
                                    style={{
                                      width: 18,
                                      height: refundHeight,
                                      minHeight: 4,
                                      background: monthRow.refunds > 0 ? "#dc3545" : "#f1f3f5",
                                    }}
                                />
                              </div>
                              <div className="small text-muted mt-2">{MONTH_LABELS_UA[monthIndex] || monthRow.month}</div>
                              <input
                                  className={`form-control form-control-sm text-center mt-1 ${monthRow.displayNet < 0 ? "text-danger" : "text-dark"}`}
                                  style={{ minWidth: 72, paddingLeft: 4, paddingRight: 4 }}
                                  value={draftValues[inputKey] ?? ""}
                                  disabled={Boolean(saving[inputKey])}
                                  title="Своє значення для цього місяця. Enter або вихід з поля зберігає в БД, лише якщо число змінилось. Очистіть поле — місяць знову рахується автоматично."
                                  onChange={(e) => {
                                    setDraftValues((prev) => ({ ...prev, [inputKey]: e.target.value }));
                                  }}
                                  onBlur={async () => {
                                    const currentNet = monthRow.displayNet ?? monthRow.net;
                                    const restoreDraft = () =>
                                      setDraftValues((prev) => ({ ...prev, [inputKey]: money0(currentNet) }));
                                    const raw = String(draftValues[inputKey] ?? "").trim();

                                    // Очищене поле: скинути ручне значення (повернути авторозрахунок). Для авто-місяця — нічого.
                                    if (raw === "") {
                                      if (monthRow.valueSource !== "manual") {
                                        restoreDraft();
                                        return;
                                      }
                                      setSaving((prev) => ({ ...prev, [inputKey]: true }));
                                      try {
                                        await onResetMonthValue(item.key, monthRow.month);
                                      } catch (e) {
                                        console.error("Помилка скидання значення ліміту ФОП:", e);
                                        restoreDraft();
                                      } finally {
                                        setSaving((prev) => ({ ...prev, [inputKey]: false }));
                                      }
                                      return;
                                    }

                                    const value = parseMoneyInput(raw);
                                    // Не число або число не змінилось — нічого не зберігаємо, щоб клік у поле не «закріплював» місяць.
                                    if (value == null || Math.round(value) === Math.round(Number(currentNet) || 0)) {
                                      restoreDraft();
                                      return;
                                    }
                                    setSaving((prev) => ({ ...prev, [inputKey]: true }));
                                    try {
                                      await onSaveMonthValue(item.key, monthRow.month, value, monthRow);
                                      setDraftValues((prev) => ({ ...prev, [inputKey]: money0(value) }));
                                    } catch (e) {
                                      console.error("Помилка збереження значення ліміту ФОП:", e);
                                      restoreDraft();
                                    } finally {
                                      setSaving((prev) => ({ ...prev, [inputKey]: false }));
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.currentTarget.blur();
                                    }
                                  }}
                              />
                            </div>
                        );
                        })}
                      </FopMonthsScroller>
                    </div>
                    <div className="d-flex flex-wrap gap-3 small text-muted mt-2">
                      <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, background: "#111827" }} />Monobank надходження</span>
                      <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, background: "#315bdc" }} />NovaPay надходження</span>
                      <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, background: "#f59f00" }} />Готівка Київ</span>
                      <span><span className="d-inline-block me-1" style={{ width: 10, height: 10, background: "#dc3545" }} />Повернення</span>
                    </div>
                  </div>
                </div>
              </div>
          );
        })}
      </div>
  );
}

export const Obig = ({user}) => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const [mono, setMono] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [novapay, setNovaPay] = useState([]);
  const [novaPayLoadError, setNovaPayLoadError] = useState("");
  const [limits, setLimits] = useState([]);
  const [currency, setCurrency] = useState("UAH");
  const [usdBuyRate, setUsdBuyRate] = useState(null);
  const [usdSellRate, setUsdSellRate] = useState(null);
  const [showClass, setShowClass] = useState(false);
  const [purchaseDebtUAH, setPurchaseDebtUAH] = useState(null);
  const [purchaseDebtBySupplier, setPurchaseDebtBySupplier] = useState([]);
  const [fopTurnover, setFopTurnover] = useState(null);
  const [fopTurnoverLoading, setFopTurnoverLoading] = useState(true);
  const [fopTurnoverError, setFopTurnoverError] = useState("");
  const [fopYearLimitInput, setFopYearLimitInput] = useState("");
  const [savingFopYearLimit, setSavingFopYearLimit] = useState(false);
  const [selectedFopYear, setSelectedFopYear] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1);
  });

  const isAdmin = user?.role === "admin";
  const { labelBySlug } = useFopLabels();
  const activeTabConfig = OBIG_TAB_BY_SLUG.get(tabParam) || DEFAULT_OBIG_TAB;
  const activeTab = activeTabConfig.id;

  useEffect(() => {
    if (tabParam && !OBIG_TAB_BY_SLUG.has(tabParam)) {
      navigate(`/obig/${DEFAULT_OBIG_TAB.slug}`, { replace: true });
    }
  }, [navigate, tabParam]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await fetch("/api/exchange-rates");
        const data = await res.json();
        const usd = data.find(
            (c) => c.currencyCodeA === 840 && c.currencyCodeB === 980
        );
        if (usd) {
          setUsdBuyRate(usd.rateBuy || usd.rateCross);
          setUsdSellRate(usd.rateSell || usd.rateCross);
        }
      } catch (e) {
        console.error("Курс валют (Mono):", e);
      }
    };
    fetchRates();
  }, []);

  const monoWithNova = useMemo(() => {
    const normalizeName = (name) =>
        name
            .toLowerCase()
            .replace(/фоп|власна|ік|вячеславівна|сергіївна|миколаївна/gi, '')
            .replace(/[^\p{L}\s]/gu, '')
            .replace(/\s+/g, ' ')
            .trim();

    const formatFopName = (rawName) => {
      if (!rawName || typeof rawName !== 'string') return 'ФОП Без Імені';
      const parts = rawName.trim().split(' ');
      const nameParts = parts.filter(p => !/фоп|ік|власна/gi.test(p));
      return 'ФОП ' + nameParts.map(p => p[0].toUpperCase() + p.slice(1).toLowerCase()).join(' ');
    };

    const displayName = (slug, rawMonoName) =>
        (slug && labelBySlug[slug]) || formatFopName(rawMonoName);

    const monoNormalized = mono.map(person => {
      const slug = person.fopSlug;
      const name = displayName(slug, person.name);
      const accounts = (person.accounts || []).map(a => ({
        type: a.type,
        balance: a.balance || 0,
        creditLimit: a.creditLimit,
        currencyCode: a.currencyCode,
        maskedPan: a.maskedPan,
        iban: a.iban,
      }));
      return { name, accounts };
    });

    const novapayNormalized = novapay.map(n => ({
      name: displayName(n.key, n.name),
      accounts: [{
        type: "novapay",
        balance: n.balance,
        error: n.error || n.balance == null,
        errorMessage: n.errorMessage,
      }]
    }));

    const mergedMap = new Map();

    monoNormalized.forEach(p => {
      const key = normalizeName(p.name);
      mergedMap.set(key, { name: p.name, accounts: [...p.accounts] });
    });

    novapayNormalized.forEach(n => {
      const key = normalizeName(n.name);
      if (mergedMap.has(key)) {
        mergedMap.get(key).accounts.push(...n.accounts);
      } else {
        mergedMap.set(key, { name: n.name, accounts: [...n.accounts] });
      }
    });

    return Array.from(mergedMap.values()).map((p) => ({
      ...p,
      accounts: dedupeBlackMonobankAccounts(p.accounts || []),
    }));
  }, [mono, novapay, labelBySlug]);

  const fetchLimits = async () => {
    try {
      const res = await fetch("/api/limits/foreign-spend");
      const data = await res.json();
      setLimits(data);
    } catch (err) {
      console.error("Помилка завантаження лімітів:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`/api/payments/`);
        if (!response.ok) {
          throw new Error(`Помилка сервера: ${response.status}`);
        }
        const data = await response.json();
        // console.log("mono data:", data)
        setMono(data);
      } catch (err) {
        console.error("Помилка завантаження даних:", err);
        setError("Помилка завантаження даних. Спробуйте пізніше.");
      } finally {
        setLoading(false);
      }
    };



    fetch("/api/exchange-rates")
      .then((r) => r.json())
      .then((data) => {
        const usd = data.find((c) => c.currencyCodeA === 840 && c.currencyCodeB === 980);
        if (usd) {
          setUsdBuyRate(usd.rateBuy || usd.rateCross);
          setUsdSellRate(usd.rateSell || usd.rateCross);
        }
      })
      .catch(() => {});

    let novaPayRetryTimeout = null;

    const scheduleNovaPayRetry = (attempt, reason) => {
      const delayMs = NOVAPAY_RETRY_DELAYS_MS[attempt];
      if (!delayMs) return;

      console.warn(
          `[Obig/NovaPay] ${reason}. Повторний запит #${attempt + 1} через ${delayMs / 1000}с`
      );
      novaPayRetryTimeout = window.setTimeout(() => fetchNovaPay(attempt + 1), delayMs);
    };

    const fetchNovaPay = async (attempt = 0) => {
      try {
        const res = await fetch("/api/novapay/balances");
        if (!res.ok) {
          throw new Error(`NovaPay balances ${res.status}`);
        }

        const data = await res.json();
        if (!Array.isArray(data)) {
          throw new Error("Некоректна відповідь NovaPay balances");
        }

        const failedRows = data.filter((row) => row?.error || row?.balance == null);
        failedRows.forEach((row) => {
          console.warn("[Obig/NovaPay] Баланс недоступний", {
            key: row.key,
            name: row.name,
            errorMessage: row.errorMessage,
            attempts: row.attempts,
          });
        });

        setNovaPay(data);
        setNovaPayLoadError("");

        if (failedRows.length > 0) {
          scheduleNovaPayRetry(attempt, `Не всі баланси NovaPay завантажились (${failedRows.length})`);
        }
      } catch (err) {
        console.error("Помилка NovaPay:", err);
        setNovaPayLoadError(err.message || "Помилка NovaPay");
        scheduleNovaPayRetry(attempt, "Запит балансів NovaPay впав");
      }
    };

    const fetchPurchaseDebt = async () => {
      try {
        const res = await fetch("/api/purchases/debt-summary");
        if (!res.ok) {
          throw new Error(`debt-summary ${res.status}`);
        }
        const data = await res.json();
        setPurchaseDebtUAH(
            typeof data.totalDebtUAH === "number" ? data.totalDebtUAH : 0
        );
        setPurchaseDebtBySupplier(
            Array.isArray(data.bySupplier) ? data.bySupplier : []
        );
      } catch (err) {
        console.error("Помилка боргу по закупках:", err);
        setPurchaseDebtUAH(null);
        setPurchaseDebtBySupplier([]);
      }
    };

    fetchData();
    fetchLimits();
    fetchNovaPay();
    fetchPurchaseDebt();

    return () => {
      if (novaPayRetryTimeout) {
        window.clearTimeout(novaPayRetryTimeout);
      }
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchFopTurnover = async () => {
      setFopTurnoverLoading(true);
      setFopTurnoverError("");
      try {
        const year = selectedFopYear.getFullYear();
        const res = await fetch(`/api/limits/fop-turnover?year=${year}`, {
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`fop-turnover ${res.status}`);
        }
        const data = await res.json();
        setFopTurnover(data);
        setFopYearLimitInput(money0(data.limit));
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Помилка лімітів ФОПів:", err);
        setFopTurnoverError("Не вдалося завантажити ліміти ФОПів");
      } finally {
        if (!controller.signal.aborted) {
          setFopTurnoverLoading(false);
        }
      }
    };

    fetchFopTurnover();

    return () => controller.abort();
  }, [selectedFopYear]);

  const fmt = (v) =>
      Number(v || 0).toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

  const toUsd = (uah) => {
    if (!usdSellRate) return null;
    return uah / usdSellRate;
  };

  if (loading) {
    return (
        <div
            className="d-flex flex-grow-1 justify-content-center align-items-center bg-body w-100"
            style={{ minHeight: 0 }}
        >
            <Spinner
                animation="border"
                role="status"
                aria-busy="true"
                className="text-black"
                style={{ width: "3rem", height: "3rem" }}
            />
        </div>
    );
  }
  if (error) return <p style={{ color: "red" }}>{error}</p>;
  // Собираем все карты в один массив
  const allCards = mono.flatMap(person => person.accounts || []);
  const monoCards = allCards.filter(isRegularObigMonoCard);

  const totalMonoUahBalance = monoCards
      .filter(isUahMonoCard)
      .reduce((acc, card) => acc + getMonoOwnBalanceMinor(card), 0) / 100;
  const totalMonoUsdBalance = monoCards
      .filter(isUsdMonoCard)
      .reduce((acc, card) => acc + getMonoOwnBalanceMinor(card), 0) / 100;
  const totalMonoUsdBalanceUah = usdSellRate ? totalMonoUsdBalance * usdSellRate : 0;
  const totalMonoBalance = totalMonoUahBalance + totalMonoUsdBalanceUah;
  const novaPayHasError = Boolean(novaPayLoadError) || novapay.some(n => n.error || n.balance == null);
  const totalNovaBalance = novapay.reduce((acc, n) => acc + (n.error || n.balance == null ? 0 : n.balance), 0);
  const totalBalance = totalMonoBalance + totalNovaBalance;

  const saveFopMonthValue = async (fopKey, month, value, monthRow = {}) => {
    const metrics = {
      monobankIncome: Number(monthRow.monobankIncome) || 0,
      novapayIncome: Number(monthRow.novapayIncome) || 0,
      cashIncome: Number(monthRow.cashIncome) || 0,
      refunds: Number(monthRow.refunds) || 0,
      totalIncome: Number(monthRow.totalIncome) || 0,
      net: Number(monthRow.net) || 0,
    };
    const res = await fetch("/api/limits/fop-turnover/override", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ fopKey, month, value, metrics }),
    });
    if (!res.ok) {
      throw new Error("Не вдалося зберегти значення");
    }
    const saved = await res.json();
    setFopTurnover((prev) => {
      if (!prev?.items) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => {
          if (item.key !== saved.fopKey) return item;
          const months = (item.months || []).map((m) =>
              m.month === saved.month
                  ? { ...m, ...(saved.metrics || metrics), overrideNet: saved.value, displayNet: saved.value, valueSource: "manual" }
                  : m
          );
          const used = months.reduce((sum, m) => sum + (m.displayNet || 0), 0);
          return {
            ...item,
            used,
            remaining: Math.max(0, (item.limit || 0) - used),
            months,
          };
        }),
      };
    });
  };

  /** Скидає ручне значення місяця і перечитує розрахунок з сервера (без спінера). */
  const resetFopMonthValue = async (fopKey, month) => {
    const qs = new URLSearchParams({ fopKey, month });
    const res = await fetch(`/api/limits/fop-turnover/override?${qs}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error("Не вдалося скинути значення");
    }
    const year = selectedFopYear.getFullYear();
    const fresh = await fetch(`/api/limits/fop-turnover?year=${year}`, { credentials: "include" });
    if (!fresh.ok) {
      throw new Error("Не вдалося оновити ліміти ФОПів");
    }
    setFopTurnover(await fresh.json());
  };

  const saveFopYearLimit = async () => {
    const value = parseMoneyInput(fopYearLimitInput);
    if (value == null || value <= 0) {
      setFopYearLimitInput(money0(fopTurnover?.limit || 0));
      return;
    }

    setSavingFopYearLimit(true);
    try {
      const year = selectedFopYear.getFullYear();
      const res = await fetch("/api/limits/fop-turnover/year-limit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ year, limit: value }),
      });
      if (!res.ok) {
        throw new Error("Не вдалося зберегти річний ліміт");
      }
      const saved = await res.json();
      setFopYearLimitInput(money0(saved.limit));
      setFopTurnover((prev) => {
        if (!prev?.items) return prev;
        return {
          ...prev,
          limit: saved.limit,
          items: prev.items.map((item) => ({
            ...item,
            limit: saved.limit,
            remaining: Math.max(0, saved.limit - (item.used || 0)),
          })),
        };
      });
    } catch (err) {
      console.error("Помилка збереження річного ліміту ФОП:", err);
      setFopYearLimitInput(money0(fopTurnover?.limit || 0));
    } finally {
      setSavingFopYearLimit(false);
    }
  };

  return (
      <main style={{ padding: "1rem 0" }}>
        <ObigPurchaseDebtBar
            totalDebtUAH={purchaseDebtUAH}
            bySupplier={purchaseDebtBySupplier}
            displayCurrency={currency}
            usdSellRate={usdSellRate}
        />
        <div className="container px-3">
          <Nav
              variant="tabs"
              className="mb-3 flex-nowrap overflow-auto"
              activeKey={activeTab}
              onSelect={(k) => {
                const tab = OBIG_TAB_BY_ID.get(k);
                if (tab) navigate(`/obig/${tab.slug}`);
              }}
          >
            {OBIG_TABS.map((tab) => (
                <Nav.Item key={tab.id}>
                  <Nav.Link eventKey={tab.id}>{tab.label}</Nav.Link>
                </Nav.Item>
            ))}
          </Nav>
        </div>

        {activeTab === "balance" && (
            <>
              <div className="container">
                <div className="px-3 mb-3">
                  {isAdmin ? (
                      <div className="d-flex flex-column gap-1">
                        <h3 className="mb-1">
                          {"Баланс коштів: " + Math.floor(totalBalance).toLocaleString() + " грн."}
                          {usdSellRate && (
                              <span className="text-muted fs-6 ms-2">
                                (~${fmt(toUsd(Math.floor(totalBalance)))})
                              </span>
                          )}
                        </h3>

                        <div className="fst-italic d-flex flex-column gap-1">
                          <div>
                            Monobank UAH:{" "}
                            <strong>{Math.floor(totalMonoUahBalance).toLocaleString()}</strong> грн.
                            {usdSellRate && (
                                <span className="text-muted small ms-1">
                                  (~${fmt(toUsd(Math.floor(totalMonoUahBalance)))})
                                </span>
                            )}
                          </div>
                          <div>
                            Доларові картки:{" "}
                            <strong>${fmt(totalMonoUsdBalance)}</strong>
                            {usdSellRate && (
                                <span className="text-muted small ms-1">
                                  (~{Math.floor(totalMonoUsdBalanceUah).toLocaleString()} грн.)
                                </span>
                            )}
                          </div>
                          <div>
                            Novapay:{" "}
                            <strong>{totalNovaBalance.toLocaleString()}</strong> грн.
                            {novaPayHasError && (
                                <span className="text-danger small ms-1">
                                  ⚠ деякі баланси недоступні, повторюю запит
                                </span>
                            )}
                            {usdSellRate && !novaPayHasError && (
                                <span className="text-muted small ms-1">
                                  (~${fmt(toUsd(Math.floor(totalNovaBalance)))})
                                </span>
                            )}
                          </div>

                          {showClass && (() => {
                            const PERSONAL_SLUGS = ["нежид", "ірин", "плеханов", "татарін"];
                            const personalBalanceKop = monoWithNova
                              .filter(p => PERSONAL_SLUGS.some(n => p.name.toLowerCase().includes(n)))
                              .reduce((sum, p) => {
                                const blackAcc = p.accounts.filter(a => a.type === "black" && Number(a.currencyCode) === 980);
                                return sum + blackAcc.reduce((s, a) => s + Math.max(0, (a.balance || 0) - (a.creditLimit || 0)), 0);
                              }, 0);
                            const personalBalanceUah = Math.floor(personalBalanceKop / 100);
                            return (
                              <div>
                                Баланс особистих коштів:{" "}
                                <strong>{personalBalanceUah.toLocaleString()}</strong> грн
                                {usdSellRate && (
                                  <span className="text-muted small ms-1">(~${fmt(toUsd(personalBalanceUah))})</span>
                                )}
                              </div>
                            );
                          })()}
                        </div>

                        <div className="mt-2">
                          <Button
                              variant="outline-secondary"
                              onClick={() => setShowClass(prev => !prev)}
                          >
                            {showClass ? "Сховати чорні картки" : "Показати чорні картки"}
                          </Button>
                        </div>
                      </div>
                  ) : (
                      <div className="my-5">
                        <h2 className="mb-2">💵 Баланс коштів</h2>
                      </div>
                  )}
                </div>
              </div>
              <div className="container">
                <div className="inner-flex d-flex">
                  <div className="col-inner flex-grow-1 px-3">
                    <Accordion defaultActiveKey={"0"} className="lava-accordion">
                      {monoWithNova.map((person, p) => (
                          <Accordion.Item key={p} eventKey={String(p)}>
                            <Accordion.Header style={{ fontSize: "20px" }}>
                              {person.name}
                            </Accordion.Header>
                            <Accordion.Body>
                              {person.accounts.filter((a) => {
                                if (a.type === "eAid" || a.type === "madeInUkraine" || a.type === "diia") {
                                  return false;
                                }
                                if (isUsdMonoCard(a)) {
                                  return true;
                                }
                                const isAdminCard = ADMIN_ONLY_CARDS.includes(a.type);

                                // если это спец карта
                                if (isAdminCard) {
                                  return isAdmin && showClass; // показываем только если включено
                                }

                                return true; // обычные всегда показываем
                              }).map((a, i) => {
                                const isAdminCard = ADMIN_ONLY_CARDS.includes(a.type);
                                const isUsdCard = isUsdMonoCard(a);
                                const ownBalanceMinor = getMonoOwnBalanceMinor(a);
                                const ownBalance = ownBalanceMinor / 100;

                                return (
                                    <p key={i} className={`${a.type} ${isAdminCard && (showClass || isUsdCard) ? "show" : ""}`}>
                                      {a.type === "novapay" ? (
                                          <>
                                            NovaPay баланс:{" "}
                                            {a.error ? (
                                                <span className="text-danger" style={{ fontSize: "16px" }}>
                                                  Помилка отримання{a.errorMessage ? `: ${a.errorMessage}` : ""}
                                                </span>
                                            ) : (
                                                <>
                                                  <span style={{ fontSize: "20px" }}>{Math.floor(a.balance).toLocaleString()}</span>{" "}грн.
                                                  {usdSellRate && (
                                                      <span className="text-muted small ms-1">
                                                        (~${fmt(toUsd(Math.floor(a.balance)))})
                                                      </span>
                                                  )}
                                                </>
                                            )}
                                          </>
                                      ) : (
                                          <>
                                            {formatMonoCardLabel(a, isUsdCard)}
                                            {": "}
                                            <span style={{ fontSize: "20px" }}>
                                              {isUsdCard ? fmt(ownBalance) : Math.floor(ownBalance).toLocaleString()}
                                            </span>{" "}
                                            {isUsdCard ? "USD" : "грн"}
                                            <span className="text-muted small ms-1">
                                              {formatBlackCreditUsageUa(a)}
                                            </span>
                                            {usdSellRate && !isUsdCard && (
                                                <span className="ms-1">
                                                  (~${fmt(toUsd(ownBalance))})
                                                </span>
                                            )}
                                            {usdSellRate && isUsdCard && (
                                                <span className="text-muted small ms-1">
                                                  (~{Math.floor(ownBalance * usdSellRate).toLocaleString()} грн.)
                                                </span>
                                            )}
                                          </>
                                      )}
                                    </p>
                                );
                              })}
                            </Accordion.Body>
                          </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                </div>
              </div>
            </>
        )}

        {activeTab === "foreign" && (
            <div className="container">
              <div className="px-3">
                <ForeignSpendAccordion limits={limits} user={user} currency={currency} usdSellRate={usdSellRate}/>
              </div>
            </div>
        )}

        {activeTab === "fopLimits" && (
            <div className="container">
              <div className="px-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <h3 className="mb-0">Ліміти ФОПів</h3>
                  <div className="d-flex flex-wrap align-items-end gap-2">
                    <div>
                      <label className="form-label small text-muted mb-1">Річний ліміт</label>
                      <input
                          className="form-control form-control-sm text-end"
                          style={{ width: 150 }}
                          value={fopYearLimitInput}
                          disabled={savingFopYearLimit}
                          onChange={(e) => setFopYearLimitInput(e.target.value)}
                          onBlur={saveFopYearLimit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.currentTarget.blur();
                            }
                          }}
                      />
                    </div>
                    <div>
                      <label className="form-label small text-muted mb-1">Рік</label>
                      <div style={{ width: 110 }}>
                        <DatePicker
                            selected={selectedFopYear}
                            onChange={(date) => {
                              if (date) {
                                setSelectedFopYear(new Date(date.getFullYear(), 0, 1));
                              }
                            }}
                            showYearPicker
                            dateFormat="yyyy"
                            className="form-control form-control-sm text-center"
                            popperPlacement="bottom-end"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <FopLimitsCards
                    data={fopTurnover}
                    loading={fopTurnoverLoading}
                    error={fopTurnoverError}
                    labelBySlug={labelBySlug}
                    onSaveMonthValue={saveFopMonthValue}
                    onResetMonthValue={resetFopMonthValue}
                />
              </div>
            </div>
        )}
      </main>
  );
};
